/* eslint-disable */
/* eslint-disable no-console */
/* eslint-disable no-useless-escape */
import moment from 'moment';

// Constants
export const TEXT_MESSAGE_TYPE = 'RAFTXT';
export const TEXT_MESSAGE_TEMPLATE = 'RAF-ADVOCATE-LINK';

// Interfaces
export interface CustomerPromotionProfile {
  referralCode?: string;
  promoProgramName?: string;
  promotionCode?: string;
  advocateReferralUrl?: string;
  pendingReward?: string;
  availableReward?: string;
  referrerCustomerArray?: any[];
  referredByCustomer?: any[];
}

export interface RewardFaceValue {
  advocateFaceValue?: string;
  referralFaceValue?: string;
}

export interface ReferralLinkInput {
  isEmailSelected: boolean;
  isTextSelected: boolean;
  emailTextValue: string;
  phoneTextValue: string;
}

export interface ReferralDetail {
  name: string;
  refferedOn: string;
  rewardEarned: any;
  rewardUsedOn: string;
  contactId: string;
}

export interface CustomerInfo {
  globalCustomerId?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  emailAddress?: string;
}

export interface RewardProfilePayload {
  globalCustomerId: string;
  lob: string;
  includeBalance: string;
  includeReferral: string;
  includeProgram: string;
  includeReferredBy: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  storeNumbers: (string | null)[];
}

// Validation functions
export const EMAIL_REGEX = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;

export const validateEmail = (email: string): boolean => {
  return EMAIL_REGEX.test(email);
};

export const validatePhoneNumber = (phone: string): boolean => {
  return phone.replace(/\D/g, '').length >= 10;
};

// Formatting functions
export const phoneNumberFormat = (value: string): string => {
  return value
    .replace(/\D/g, '')
    .replace(
      /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/,
      '($1) $2-$3'
    );
};

export const stripPhoneNumber = (value: string): string => {
  return value.replace(/\D/g, '');
};

export const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  return moment(dateString.split("T")[0])?.format("MM/DD/YYYY") ?? '-';
};

// Reward Profile Payload Builder
export const buildRewardProfilePayload = (customerInfo: CustomerInfo, storeNumber: string | null): RewardProfilePayload => {
  return {
    globalCustomerId: String(customerInfo?.globalCustomerId),
    lob: 'RTO',
    includeBalance: 'Y',
    includeReferral: 'Y',
    includeProgram: 'Y',
    includeReferredBy: 'Y',
    firstName: customerInfo?.firstName ?? undefined,
    lastName: customerInfo?.lastName ?? undefined,
    email: customerInfo?.emailAddress ?? undefined,
    phoneNumber: customerInfo?.phoneNumber ?? undefined,
    storeNumbers: [storeNumber]
  };
};

// Process referral summary from enrolled promotion list
export const processReferralSummary = (enrolledPromotionList: any[], gridMock: any): ReferralDetail[] => {
  const referrerCustomerArray: ReferralDetail[] = [];
  
  if (enrolledPromotionList && enrolledPromotionList.length > 0) {
    const referralSummary = enrolledPromotionList[0]?.referralSummary;
    if (referralSummary && referralSummary.length > 0) {
      referralSummary.forEach((val: any) => {
        referrerCustomerArray.push({
          name: val?.referredPartyName,
          refferedOn: formatDate(val?.referredOnDate),
          rewardEarned: enrolledPromotionList[0]?.referralFaceValue,
          rewardUsedOn: val?.advocateRwrdErndDate ? formatDate(val?.advocateRwrdErndDate) : '-',
          contactId: val?.referredPartyContactId ?? ''
        });
      });
    } else {
      referrerCustomerArray.push(gridMock);
    }
  } else {
    referrerCustomerArray.push(gridMock);
  }
  
  return referrerCustomerArray;
};

// Process referred by list
export const processReferredByList = (referredByList: any[], gridMock: any): ReferralDetail[] => {
  const referredByCustomer: ReferralDetail[] = [];
  
  if (referredByList && referredByList.length > 0) {
    referredByList.forEach((val: any) => {
      referredByCustomer.push({
        name: `${val?.firstName ?? ''} ${val?.lastName ?? ''}`,
        refferedOn: formatDate(val?.referredOnDate),
        rewardEarned: val?.referralFaceValue,
        rewardUsedOn: val?.advocateRwrdErndDate ? formatDate(val?.advocateRwrdErndDate) : '-',
        contactId: val?.contactId ?? ''
      });
    });
  } else {
    referredByCustomer.push(gridMock);
  }
  
  return referredByCustomer;
};

// Extract referral code and URL from enrolled promotion list
export const extractEnrolledPromotionData = (enrolledPromotionList: any[]): { referralCode: string; advocateReferralUrl: string | undefined } => {
  if (enrolledPromotionList && enrolledPromotionList.length > 0) {
    return {
      referralCode: enrolledPromotionList[0]?.referralCode ?? '-',
      advocateReferralUrl: enrolledPromotionList[0]?.referralUrl
    };
  }
  return { referralCode: '-', advocateReferralUrl: undefined };
};

// Extract promotion code from eligible promotion list
export const extractPromotionCode = (eligiblePromotionList: any[]): string | undefined => {
  if (eligiblePromotionList && eligiblePromotionList.length > 0) {
    return eligiblePromotionList[0]?.promotionCode;
  }
  return undefined;
};

// Calculate available reward
export const calculateAvailableReward = (
  referredByList: any[],
  totalPendingAmount: string | undefined,
  totalAvailableBalance: string | undefined
): string => {
  if (referredByList && referredByList.length > 0 && referredByList[0]?.status === 'NOTREDEEMED') {
    const pending = Number(totalPendingAmount ?? 0);
    const faceValue = Number(referredByList[0]?.referralFaceValue ?? 0);
    return (pending + faceValue).toFixed(2);
  }
  return totalAvailableBalance ?? '0.00';
};

// Build promotion profile from reward details response
export const buildPromotionProfile = (rewardDetails: any, gridMock: any): CustomerPromotionProfile => {
  const data = rewardDetails?.data;
  
  if (!data) {
    return {
      promotionCode: '',
      promoProgramName: '',
      pendingReward: '0.00',
      availableReward: '0.00',
      referralCode: '-',
      referrerCustomerArray: [gridMock],
      referredByCustomer: [gridMock],
      advocateReferralUrl: ''
    };
  }

  const referralProgram = data?.referralProgram;
  const enrolledPromotionList = referralProgram?.enrolledPromotionList || [];
  const referredByList = referralProgram?.referredByList || [];
  const eligiblePromotionList = referralProgram?.eligiblePromotionList || [];

  const referrerCustomerArray = processReferralSummary(enrolledPromotionList, gridMock);
  const referredByCustomer = processReferredByList(referredByList, gridMock);
  const { referralCode, advocateReferralUrl } = extractEnrolledPromotionData(enrolledPromotionList);
  const promotionCode = extractPromotionCode(eligiblePromotionList);
  const availableReward = calculateAvailableReward(referredByList, data?.totalPendingAmount, data?.totalAvailableBalance);

  return {
    promotionCode,
    promoProgramName: referralProgram?.programName ?? '',
    pendingReward: data?.totalPendingAmount === '0' ? '0.00' : (data?.totalPendingAmount ?? '0.00'),
    availableReward: availableReward ?? '0.00',
    referralCode,
    referrerCustomerArray,
    referredByCustomer,
    advocateReferralUrl
  };
};

// Check if reward details response is successful
export const isRewardDetailsSuccess = (rewardDetails: any): boolean => {
  return rewardDetails?.status === 200 || rewardDetails?.status === 204;
};

// Send Referral Link Payload Builders
export interface EnrollPayloadParams {
  customerPromotionProfile: CustomerPromotionProfile[];
  globalCustomerId: string;
  isEmailSelected: boolean;
  isTextSelected: boolean;
  storeNumber: string | null;
}

export interface EmailPayloadParams {
  customerPromotionProfile: CustomerPromotionProfile[];
  customerFirstName: string;
  customerLastName: string;
  state: string;
  phoneNumber: string;
  emailAddress: string;
  globalCustomerId: string;
}

export interface TextPayloadParams {
  customerPromotionProfile: CustomerPromotionProfile[];
  customerFirstName: string;
  customerLastName: string;
  phoneNum: string;
  customerId: string;
  globalCustomerId: string;
  rewardFaceValue?: RewardFaceValue;
}

export const buildEnrollPayload = (params: EnrollPayloadParams): any => {
  const { customerPromotionProfile, globalCustomerId, isEmailSelected, isTextSelected, storeNumber } = params;
  return {
    enrollInfo: {
      promoProgramName: customerPromotionProfile[0]?.promoProgramName,
      promotionCode: customerPromotionProfile[0]?.promotionCode,
      globalCustomerId: globalCustomerId,
      ...(isEmailSelected && { notifyByEmail: 'Y' }),
      ...(isTextSelected && { notifyByText: 'Y' }),
      channel: "POS",
      storeNumber: storeNumber
    },
    isEnroll: true
  };
};

export const buildEmailPayload = (params: EmailPayloadParams): any => {
  const { customerPromotionProfile, customerFirstName, customerLastName, state, phoneNumber, emailAddress, globalCustomerId } = params;
  return {
    isEmailFlag: true,
    advocateEmailInfo: {
      firstName: customerFirstName,
      lastName: customerLastName,
      metaData: {
        advocate_link: customerPromotionProfile[0]?.advocateReferralUrl,
        language: '1',
        first_name: customerFirstName ?? '',
        last_name: customerLastName ?? '',
        customer_State: state ?? '',
        customer_Phone_number: phoneNumber,
        email_address: emailAddress,
        global_customer_id: globalCustomerId
      },
      emailAddress: emailAddress,
      eventKey: 'Advocate_Link',
      communicationType: 'EMAIL',
      globalCustomerId: globalCustomerId
    }
  };
};

export const buildTextPayload = (params: TextPayloadParams): any => {
  const { customerPromotionProfile, customerFirstName, customerLastName, phoneNum, customerId, globalCustomerId, rewardFaceValue } = params;
  return {
    isTextFlag: true,
    advocateTextInfo: {
      language: "ENG",
      phoneNumber: phoneNum,
      messageType: TEXT_MESSAGE_TYPE,
      messageTemplateKey: TEXT_MESSAGE_TEMPLATE,
      messageSubstitutes: {
        firstName: customerFirstName ?? '',
        lastName: customerLastName ?? '',
        referralLink: customerPromotionProfile[0]?.advocateReferralUrl,
        advocateAmount: rewardFaceValue?.advocateFaceValue,
        refferalAmount: rewardFaceValue?.referralFaceValue
      },
      callBackParams: {
        customerId: customerId,
        coworkerFirstName: null,
        coworkerLastName: null,
        employeeId: null
      },
      storeNumber: window.sessionStorage.getItem('storeNumber'),
      transactionId: globalCustomerId
    }
  };
};

// Check if customer needs enrollment
export const needsEnrollment = (customerPromotionProfile: CustomerPromotionProfile[]): boolean => {
  return customerPromotionProfile[0]?.referralCode === '-' || !customerPromotionProfile[0]?.referralCode;
};
